import { Client, type Room } from '@colyseus/sdk';
import {
  BATTLE_ROOM,
  ClientMessage,
  DEFAULT_BATTLE_SERVER_URL,
  ServerMessage,
  type BattleJoinOptions,
  type BattlePhase,
  type MovePayload,
  type WelcomePayload,
} from '@dungeon-builder/shared';

/**
 * Read-only mirror of the server's `BattleState`.
 *
 * Declared structurally rather than imported from `@dungeon-builder/shared/battle-state`:
 * @colyseus/sdk bundles its own copy of @colyseus/schema, so sharing the class
 * would put two schema runtimes in one bundle. Keep this in sync by hand -- the
 * field names are the wire contract.
 */
export interface PlayerView {
  sessionId: string;
  displayName: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  hp: number;
  connected: boolean;

  // Appearance, mirrored field-for-field from PlayerState. A character is a record of
  // choices, so a peer renders from these and downloads nothing extra.
  gender: string;
  stance: string;
  packId: string;
  /** Catalogue variant ids, comma separated -- see `decodeAppearance`. */
  appearance: string;
  /** `region=#rrggbb` pairs -- see `decodeTints`. */
  tints: string;
  colourway: string;
}

export interface BattleStateView {
  levelId: string;
  levelName: string;
  phase: BattlePhase;
  elapsedMs: number;
  players: Map<string, PlayerView>;
}

export type ConnectionStatus = 'connecting' | 'online' | 'offline';

export interface BattleConnectionEvents {
  onStatus?(status: ConnectionStatus, detail?: string): void;
  onState?(state: BattleStateView): void;
  onWelcome?(payload: WelcomePayload): void;
}

export class BattleConnection {
  private readonly client: Client;
  private room: Room<any, BattleStateView> | undefined;

  constructor(
    private readonly events: BattleConnectionEvents = {},
    url: string = DEFAULT_BATTLE_SERVER_URL,
  ) {
    this.client = new Client(url);
  }

  get sessionId(): string | undefined {
    return this.room?.sessionId;
  }

  async connect(options: BattleJoinOptions = {}): Promise<void> {
    this.events.onStatus?.('connecting');
    try {
      const room = await this.client.joinOrCreate<BattleStateView>(BATTLE_ROOM, options);
      this.room = room;

      room.onMessage<WelcomePayload>(ServerMessage.Welcome, (payload) => {
        this.events.onWelcome?.(payload);
      });

      room.onStateChange((state) => this.events.onState?.(state));

      room.onLeave((code) => {
        this.room = undefined;
        this.events.onStatus?.('offline', `left (code ${code})`);
      });

      room.onError((code, message) => {
        this.events.onStatus?.('offline', `error ${code}: ${message ?? 'unknown'}`);
      });

      this.events.onStatus?.('online');
    } catch (error) {
      this.room = undefined;
      this.events.onStatus?.('offline', error instanceof Error ? error.message : String(error));
    }
  }

  sendMove(payload: MovePayload): void {
    this.room?.send(ClientMessage.Move, payload);
  }

  sendReady(): void {
    this.room?.send(ClientMessage.Ready, {});
  }

  async dispose(): Promise<void> {
    await this.room?.leave();
    this.room = undefined;
  }
}
